import { useEffect, useRef, useState } from 'react';

/**
 * One metric over the days of the window, the chart at the top of Google Ads'
 * overview.
 *
 * ONE series on ONE axis. Google's own chart puts two metrics on two y-scales,
 * which makes any two lines appear to move together; picking the metric above
 * the chart says the same thing without implying a relationship that is not
 * there. Colour is the ink token: a single series needs no identity hue, and
 * the title already names it.
 */

export type Point = { day: string; value: number | null };

const H = 220;
const PAD = { top: 12, right: 12, bottom: 26, left: 56 };

export function MirrorChart({ points, format, label }: {
  points: Point[];
  format: (v: number) => string;
  label: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    if (!box.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);

  const known = points.filter((p) => p.value != null) as { day: string; value: number }[];
  if (points.length < 2 || !known.length) {
    return <p className="py-10 text-center text-sm text-t3">Not enough days in this window to draw a line.</p>;
  }

  const max = Math.max(...known.map((p) => p.value), 0) || 1;
  const nice = niceCeil(max);
  const iw = width - PAD.left - PAD.right;
  const ih = H - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (points.length === 1 ? iw / 2 : (i / (points.length - 1)) * iw);
  const y = (v: number) => PAD.top + ih - (v / nice) * ih;

  // A gap where Google gave no number, rather than a line drawn through a
  // guess of zero.
  let d = '';
  points.forEach((p, i) => {
    if (p.value == null) return;
    const prevKnown = i > 0 && points[i - 1].value != null;
    d += `${prevKnown ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`;
  });

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * nice);
  const every = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(iw / 90))));
  const h = hover != null ? points[hover] : null;

  function move(e: React.PointerEvent<SVGSVGElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * width;
    const i = Math.round(((px - PAD.left) / iw) * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, i)));
  }

  return (
    <div ref={box} className="relative">
      <svg
        width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="img"
        aria-label={`${label} by day`}
        onPointerMove={move} onPointerLeave={() => setHover(null)}
        className="block touch-none"
      >
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--bd)" strokeWidth={1} />
            <text x={PAD.left - 8} y={y(t)} dy="0.32em" textAnchor="end" fontSize={11} fill="var(--t3)" className="num">
              {format(t)}
            </text>
          </g>
        ))}
        {points.map((p, i) => (i % every === 0 || i === points.length - 1) && (
          <text key={p.day} x={x(i)} y={H - 8} textAnchor="middle" fontSize={11} fill="var(--t3)">
            {shortDay(p.day)}
          </text>
        ))}
        <path d={d} fill="none" stroke="var(--ink)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {h && hover != null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + ih} stroke="var(--bd2)" strokeWidth={1} />
            {h.value != null && (
              <circle cx={x(hover)} cy={y(h.value)} r={4} fill="var(--ink)" stroke="var(--bg2)" strokeWidth={2} />
            )}
          </g>
        )}
      </svg>
      {h && hover != null && (
        <div
          className="pointer-events-none absolute top-1 rounded-btn border border-bd bg-bg2 px-2.5 py-1.5 text-xs shadow-card"
          style={{ left: Math.min(Math.max(x(hover) - 70, 0), width - 150) }}
        >
          <div className="text-t3">{longDay(h.day)}</div>
          <div className="num font-medium text-t1">{h.value == null ? 'Not reported' : `${label}: ${format(h.value)}`}</div>
        </div>
      )}
    </div>
  );
}

function niceCeil(v: number) {
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
const shortDay = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
const longDay = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
